import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { X, Download, Printer, FileText, Link2 } from "lucide-react";

const FONT_FAMILY =
  "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

const actionIconStyle = {
  width: "34px",
  height: "34px",
  borderRadius: "8px",
  color: "#0073A6",
  backgroundColor: "#F0F9FF",
  "&:hover": { backgroundColor: "#E0F2FE" },
};

const closeIconStyle = {
  width: "34px",
  height: "34px",
  borderRadius: "8px",
  color: "#64748B",
  backgroundColor: "#F8FAFC",
  "&:hover": { backgroundColor: "#F1F5F9", color: "#12243C" },
};

const DocumentPreviewDialog = ({
  open,
  onClose,
  content,
  title,
  onDownload,
  onPrint,
  onCopyLink,
}) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [iframeHeight, setIframeHeight] = useState(300);

  const cleanQuillOutput = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    div.querySelectorAll(".ql-align-center, .ql-align-right").forEach((el) => {
      if (el.classList.contains("ql-align-center")) {
        el.style.textAlign = "center";
        el.classList.remove("ql-align-center");
      }
      if (el.classList.contains("ql-align-right")) {
        el.style.textAlign = "right";
        el.classList.remove("ql-align-right");
      }
    });
    return div.innerHTML;
  };

  // Rendered inside its own iframe document instead of dangerouslySetInnerHTML into
  // the page — the document's own styles (e.g. Word-exported margins/padding) would
  // otherwise bleed into the surrounding dialog since a bare `body {...}` selector
  // affects the real page <body> regardless of where the <style> tag is inserted.
  const iframeDocument = `<!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <style>
          * { box-sizing: border-box; }
          body {
            margin: 0;
            padding: 28px;
            font-family: ${FONT_FAMILY};
            font-size: 13px;
            color: #333;
          }
          h1 { font-size: 16px; font-weight: bold; margin: 0.5rem 0; }
          h2 { font-size: 14px; font-weight: bold; margin: 0.4rem 0; }
          p { margin: 0.3rem 0; line-height: 1.5; }
          ul { padding-left: 1.5rem; margin: 0.5rem 0; }
          strong, b { font-weight: bold; color: #222; }
          .case-details { width: 100% !important; }
          .WordSection1 { margin: 8px !important; }
        </style>
      </head>
      <body>${cleanQuillOutput(content || "")}</body>
    </html>`;

  const handleIframeLoad = (event) => {
    const iframeDoc = event.target.contentDocument;
    if (iframeDoc?.documentElement) {
      setIframeHeight(iframeDoc.documentElement.scrollHeight);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: "16px" },
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          maxHeight: { xs: "100%", sm: "85vh" },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "16px 20px",
          borderBottom: "1px solid #F1F5F9",
          backgroundColor: "#FFFFFF",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "36px",
              height: "36px",
              borderRadius: "10px",
              backgroundColor: "#F0F9FF",
              color: "#0073A6",
              flexShrink: 0,
            }}
          >
            <FileText size={18} />
          </Box>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#12243C",
              fontFamily: FONT_FAMILY,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title || "Document Preview"}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            flexShrink: 0,
          }}
        >
          {onCopyLink && (
            <Tooltip title="Copy Link">
              <IconButton
                size="small"
                onClick={onCopyLink}
                sx={actionIconStyle}
              >
                <Link2 size={17} />
              </IconButton>
            </Tooltip>
          )}
          {onDownload && (
            <Tooltip title="Download">
              <IconButton
                size="small"
                onClick={onDownload}
                sx={actionIconStyle}
              >
                <Download size={17} />
              </IconButton>
            </Tooltip>
          )}
          {onPrint && (
            <Tooltip title="Print">
              <IconButton size="small" onClick={onPrint} sx={actionIconStyle}>
                <Printer size={17} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose} sx={closeIconStyle}>
              <X size={17} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <DialogContent
        sx={{
          padding: { xs: "16px", sm: "24px" },
          overflowY: "auto",
          backgroundColor: "#F8FAFC",
          "::-webkit-scrollbar": { width: "6px" },
          "::-webkit-scrollbar-track": { background: "#F8FAFC" },
          "::-webkit-scrollbar-thumb": {
            background: "#CBD5E1",
            borderRadius: "8px",
            "&:hover": { background: "#94A3B8" },
          },
        }}
      >
        {!content ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              padding: "48px 0",
            }}
          >
            <FileText size={32} color="#94A3B8" />
            <Typography
              sx={{
                fontSize: "14px",
                color: "#64748B",
                fontFamily: FONT_FAMILY,
              }}
            >
              No data found
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              border: "1px solid #F1F5F9",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
              overflow: "hidden",
            }}
          >
            <iframe
              title={title || "Document Preview"}
              srcDoc={iframeDocument}
              onLoad={handleIframeLoad}
              sandbox="allow-same-origin"
              style={{
                width: "100%",
                height: `${iframeHeight}px`,
                border: "none",
                display: "block",
              }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentPreviewDialog;
